import { DocumentProviderType } from 'models/documentProvider';

/**
 * Where a kind of output is stored. Mirrored by numeric value from the API
 * (REDLAB.ISOPlanner.Contracts/Primitives/DocumentOutputLocation.cs).
 */
export enum DocumentOutputPurpose {
  GeneralReports = 1,
  AuditReports = 2,
  EvidenceUploads = 3,
  ReferenceEvidence = 4,
  EmailAttachments = 5,
  KpiAttachments = 6,
  AssetDocuments = 7,
}

/**
 * The providers the KPI attachments can be stored at, for the KPI and question editors, which do not read the catalogue
 * (a manager-only endpoint). Mirrors the upload providers of DocumentOutputLocationCatalog.cs.
 */
export const kpiAttachmentProviders: DocumentProviderType[] = [
  DocumentProviderType.SharePoint,
  DocumentProviderType.GoogleDrive,
  DocumentProviderType.Nextcloud,
];

/** How the writers lay out the files inside the location. Mirrored by numeric value from the API. */
export enum DocumentOutputFolderRule {
  Flat = 0,
  PerReportDefinition = 1,
  PerAudit = 2,
  PerTask = 3,
  PerKpi = 4,
  PerAsset = 5,
}

/**
 * The binding of one output purpose: the category it writes to, the start folder inside it, its provider and its
 * template.
 */
export default class DocumentOutputLocation {
  purpose: DocumentOutputPurpose;

  listId?: number;

  listName?: string;

  listUrl?: string;

  providerType?: DocumentProviderType;

  connectionId?: number;

  connectionName?: string;

  templateLinkId?: number;

  templateName?: string;

  templateUrl?: string;

  /** The purpose uses the template of the general reports location and has none of its own. */
  templateInherited: boolean;

  /** The start folder inside the category; undefined is the category root. */
  folderDriveItemId?: string;

  folderName?: string;

  folderRule: DocumentOutputFolderRule;

  folderNameFormat?: string;

  allowedProviders: DocumentProviderType[];

  constructor() {
    this.purpose = DocumentOutputPurpose.GeneralReports;
    this.templateInherited = false;
    this.folderRule = DocumentOutputFolderRule.Flat;
    this.allowedProviders = [];
  }

  isEqual(other: DocumentOutputLocation): boolean {
    return (
      this.purpose === other.purpose &&
      this.listId === other.listId &&
      this.folderDriveItemId === other.folderDriveItemId &&
      this.templateLinkId === other.templateLinkId
    );
  }

  clone(): DocumentOutputLocation {
    const output = new DocumentOutputLocation();
    output.purpose = this.purpose;
    output.listId = this.listId;
    output.listName = this.listName;
    output.listUrl = this.listUrl;
    output.providerType = this.providerType;
    output.connectionId = this.connectionId;
    output.connectionName = this.connectionName;
    output.templateLinkId = this.templateLinkId;
    output.templateName = this.templateName;
    output.templateUrl = this.templateUrl;
    output.templateInherited = this.templateInherited;
    output.folderDriveItemId = this.folderDriveItemId;
    output.folderName = this.folderName;
    output.folderRule = this.folderRule;
    output.folderNameFormat = this.folderNameFormat;
    output.allowedProviders = [...this.allowedProviders];

    return output;
  }
}

/**
 * The category and start folder of one output purpose, readable by every signed-in user; the screens outside the
 * Reports tab read it from the summary cache to know whether a purpose is bound and to which category.
 */
export class DocumentOutputLocationSummary {
  purpose: DocumentOutputPurpose;

  listId?: number;

  listName?: string;

  providerType?: DocumentProviderType;

  folderDriveItemId?: string;

  folderName?: string;

  constructor() {
    this.purpose = DocumentOutputPurpose.GeneralReports;
  }

  clone(): DocumentOutputLocationSummary {
    const output = new DocumentOutputLocationSummary();
    output.purpose = this.purpose;
    output.listId = this.listId;
    output.listName = this.listName;
    output.providerType = this.providerType;
    output.folderDriveItemId = this.folderDriveItemId;
    output.folderName = this.folderName;

    return output;
  }
}

/**
 * The check of one location. templatePresent is undefined for a purpose without a template and
 * pdfAvailable for a purpose that writes no reports; message is the code of the first failure.
 */
export class DocumentOutputLocationStatus {
  locationSet: boolean;

  reachable: boolean;

  writeAccess: boolean;

  templatePresent?: boolean;

  pdfAvailable?: boolean;

  message?: string;

  constructor() {
    this.locationSet = false;
    this.reachable = false;
    this.writeAccess = false;
  }
}
